<script lang="ts">
  import { onMount } from "svelte"
  import { xhr, toast } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import Navigation from "./Navigation.svelte"
  import User from "./components/User.svelte"
  import { STANDARD_ROLES, FREE_ROLES } from "$app/constants"
  import { onboardingStore } from "./onboarding.svelte"
  import { appStore } from "$app/store.svelte"

  import bg from "@images/onboarding/bg-eclipse.jpg"
  import img from "@images/onboarding/invite_members.png"
  import PrevNextSkipButtons from "$app/Pages/Plans/Onboarding/components/PrevNextSkipButtons.svelte"

  let errors: any = {}

  let roles = STANDARD_ROLES
  if (appStore.subscription.subscription_type === "free") {
    roles = FREE_ROLES
  }

  onboardingStore.completed = true
  let loaded = false
  let readonly = false

  type UserTypeExt = UserType & {
    _validateFn: () => boolean
  }

  const user_hash = { name: "", email: "", role: "member" } as UserTypeExt
  let users: UserTypeExt[] = []

  async function getUsers(): Promise<void> {
    try {
      const resp = await xhr.get(
        `/plan_onboarding/${appStore.plan.id}/get_users.json`
      )

      users = Object.values(resp.data.users).map((u: UserType) => {
        return { ...u, _validateFn: () => false }
      })

      if (users.length > 1) {
        readonly = true
      }

      appStore.plan = resp.data.plan
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading your user data", "error")
    } finally {
      loaded = true
    }
  }

  function validMembers(): boolean {
    let arr: boolean[] = []
    users.forEach((u) => {
      arr.push(u._validateFn())
    })

    return arr.every((v) => v)
  }

  async function nextStep(): Promise<void> {
    if (!validMembers()) return

    onboardingStore.saving = true
    errors = {}
    let members = users
      .filter((u) => !u.id)
      .filter((u) => u.name && u.email && u.role)

    try {
      const resp = await xhr.post(
        `/plan_onboarding/${appStore.plan.id}/invite_members.json`,
        { members }
      )

      appStore.plan = resp.data.plan
    } catch (e) {
      console.error(e)
      if (e.response.data) {
        errors = e.response.data
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
      toast("There was an error saving your plan data", "error")
    } finally {
      onboardingStore.current_onboarding_step = onboardingStore.nextStepName
      onboardingStore.saving = false
    }
  }

  function removeUser(idx: number): void {
    users.splice(idx, 1)
    users = [...users]
  }

  onMount(() => {
    getUsers()
    onboardingStore.setBrowserHistory({}, "invite_members")
  })
</script>

<div class="mx-auto px-2 lg:px-6 pt-4 w-full pb-8 lg:pb-20 lg:text-left">
  <div class="px-4 w-full sm:w-2/3 lg:w-8/12 xl:w-7/12 sm:pr-12 xl:pr-16">
    <Navigation step="invite_members" on:updateStep />

    <h2 class="mt-12 text-5xl font-bold text-left text-white">
      Invite Full-Access Members
    </h2>

    <p class="mt-6 text-2xl text-white">
      {#if appStore.subscription.subscription_type === "free"}
        You can invite up to 2 additional members to view and collaborate with
        your plan now.
      {:else}
        You can invite any full-access members to view and collaborate with your
        plan now. Later you will be able to invite team members and
        recognition-only members.
      {/if}
    </p>

    {#if loaded}
      <div class="space-y-8 mt-6 md:mt-8 max-w-2xl">
        <div>
          <div class="mt-6 grid grid-cols-3 gap-y-6 gap-x-4">
            <div>
              <label for="name" class="text-white text-lg">Full Name</label>
            </div>
            <div>
              <label for="name" class="text-white text-lg">Email</label>
            </div>
            <div>
              <label for="name" class="text-white text-lg">Permission</label>
            </div>
          </div>

          {#each users as user, idx}
            <User
              {user}
              {roles}
              on:delete={() => removeUser(idx)}
              bind:validate={user._validateFn}
            />
          {/each}

          <div class="col-span-6 py-4 mt-2">
            {#if users.length < appStore.plan.full_access_total}
              <button
                class="text-gray-100 text-md hover:underline hover:text-blue-300"
                data-name="add-member-button"
                type="button"
                on:click={() => {
                  users = [...users, { ...user_hash }]
                }}
              >
                + Add Another Team Member
              </button>
            {:else}
              <span class="text-gray-100 text-sm">
                You have reached the member limit for your chosen subscription
                and cannot add any more member rows. Including your user, you
                currently have used
                <strong>{users.length}</strong>
                of your
                <strong>
                  {appStore.plan.full_access_total}
                  members
                </strong>. To add more members, you can upgrade your plan after
                completing this onboarding.
              </span>
            {/if}
          </div>

          <PrevNextSkipButtons nextStepFn={nextStep} showSkip={false} />
        </div>
      </div>
    {/if}
  </div>
</div>
<div
  class="relative w-full hidden lg:inline lg:absolute lg:inset-y-0 lg:-right-0 sm:w-1/3 lg:w-4/12 xl:w-5/12 lg:h-full bg-lwp-light-grey bg-right-bottom bg-no-repeat bg-contain"
  style="background-image: url({bg})"
>
  <img
    src={img}
    alt="Your Profile"
    class="absolute inset-0 sm:-inset-x-4 lg:index-y-8 lg:-inset-x-8 lg:inset-y-24 w-full object-fill"
  />
</div>
